import html2pdf from "html2pdf.js";
import skyAutologo from "../assets/skyAutologo.png";

/**
 * Formats a currency amount for display in the PDF.
 * Reads REACT_APP_CURRENCY_SYMBOL and REACT_APP_CURRENCY_PLACEMENT env vars.
 */
const formatCurrency = (amount) => {
  const symbol = process.env.REACT_APP_CURRENCY_SYMBOL || "$";
  const placement = process.env.REACT_APP_CURRENCY_PLACEMENT || "LEFT";
  const displaySymbol = symbol === "SIGN" ? "$" : symbol;
  const formatted = Number(amount || 0).toLocaleString();
  if (placement.toUpperCase() === "RIGHT") {
    return `${formatted} ${displaySymbol}`;
  }
  return `${displaySymbol} ${formatted}`;
};

/**
 * Formats a date string into a readable format.
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

/**
 * Generates and downloads (or views) a Seller Invoice PDF.
 *
 * @param {Object} invoice   - The seller invoice data object from the API
 * @param {string} [mode]    - 'download' (default) or 'view'
 */
export const generateSellerInvoicePdf = (invoice, mode = "download") => {
  if (!invoice) {
    console.error("Seller invoice PDF: invoice data is required");
    return;
  }

  const vehicle = invoice.vehicle || {};
  const seller = invoice.seller?.user || invoice.user || {};
  const status = invoice.invoicePaymentStatus || {};
  const totalAmount = Number(invoice.totalAmount) || 0;

  const isPaid =
    status?.slug === "paid" ||
    status?.id === 3 ||
    invoice.invoicePaymentStatusId === 3;

  const amountPaid = isPaid ? totalAmount : 0;
  const remainingAmount = isPaid ? 0 : totalAmount;

  const generatedDate = new Date().toLocaleString();

  const paidStampHtml = isPaid
    ? `
    <div style="
      position: absolute;
      left: 60px;
      bottom: 100px;
      width: 110px;
      height: 60px;
      border: 2.5px solid #dc3545;
      border-radius: 4px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 100px;
        height: 52px;
        border: 1.5px solid #dc3545;
        border-radius: 2px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      ">
        <span style="font-size: 20px; font-weight: bold; color: #dc3545; letter-spacing: 2px;">PAID</span>
        <span style="font-size: 8px; color: #dc3545; margin-top: 2px;">Sky Auto Trade</span>
      </div>
    </div>`
    : "";

  const htmlContent = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
      color: #222;
      background: #fff;
      padding: 28px;
      font-size: 12px;
      position: relative;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 28px;
    }
    .logo {
      height: 44px;
    }
    .invoice-meta {
      text-align: right;
      font-size: 11px;
      color: #555;
      line-height: 1.7;
    }
    .invoice-meta span {
      color: #222;
      font-weight: 600;
    }
    .two-col {
      display: flex;
      gap: 20px;
      margin-bottom: 24px;
    }
    .two-col > div {
      flex: 1;
    }
    .section-title {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 8px;
      color: #333;
    }
    .info-row {
      font-size: 11px;
      color: #555;
      margin-bottom: 3px;
      line-height: 1.5;
    }
    .info-row span {
      color: #222;
      font-weight: 500;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 24px;
      font-size: 11px;
    }
    thead th {
      background: #428bca;
      color: #fff;
      padding: 7px 8px;
      text-align: center;
      font-weight: 600;
    }
    tbody td {
      border: 1px solid #ddd;
      padding: 8px;
      text-align: left;
      vertical-align: top;
    }
    tbody td:last-child {
      text-align: center;
    }
    .summary-block {
      margin-left: auto;
      width: 240px;
      font-size: 11px;
    }
    .summary-row {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      color: #555;
    }
    .summary-row.total-row {
      font-size: 13px;
      font-weight: 700;
      color: #222;
      border-top: 1.5px solid #ddd;
      border-bottom: 1.5px solid #ddd;
      padding: 6px 0;
      margin: 4px 0;
    }
    .summary-row span:last-child {
      font-weight: 600;
      color: #222;
    }
    .footer {
      position: absolute;
      bottom: 12px;
      left: 28px;
      right: 28px;
      text-align: center;
      font-size: 9px;
      color: #888;
      border-top: 1px solid #eee;
      padding-top: 6px;
    }
    .stamp-wrapper {
      position: relative;
      min-height: 80px;
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <img src="${skyAutologo}" class="logo" alt="Sky Auto" />
    <div class="invoice-meta">
      Invoice #: <span>${invoice.id}</span><br/>
      Date: <span>${formatDate(invoice.createdAt)}</span><br/>
      Status: <span>${status?.name || "Pending"}</span>
    </div>
  </div>

  <!-- Vehicle Details + Bill To -->
  <div class="two-col">
    <div>
      <div class="section-title">Vehicle Details:</div>
      <div class="info-row"><span>${vehicle.name || "N/A"}</span></div>
      <div class="info-row">Lot Number: <span>${vehicle.lotNumber || "N/A"}</span></div>
      <div class="info-row">VIN Number: <span>${vehicle.vinNumber || "N/A"}</span></div>
    </div>
    <div>
      <div class="section-title">Bill To:</div>
      <div class="info-row"><span>${seller.name || "N/A"}</span></div>
      <div class="info-row">${seller.email || "N/A"}</div>
      <div class="info-row">Client ID: <span>${seller.clientId || "N/A"}</span></div>
    </div>
  </div>

  <!-- Items Table -->
  <table>
    <thead>
      <tr>
        <th style="width:30%">Description</th>
        <th style="width:27%">Lot Number</th>
        <th style="width:27%">VIN Number</th>
        <th style="width:16%">Amount</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td>${invoice.isGatepassRenewal ? "Gatepass Renewal Fee" : "Vehicle Listing Fee"}</td>
        <td>${vehicle.lotNumber || "N/A"}</td>
        <td>${vehicle.vinNumber || "N/A"}</td>
        <td style="text-align:center">${formatCurrency(totalAmount)}</td>
      </tr>
    </tbody>
  </table>

  <!-- Payment Summary + PAID Stamp -->
  <div class="stamp-wrapper">
    ${paidStampHtml}
    <div class="summary-block">
      <div class="summary-row"><span>Subtotal:</span><span>${formatCurrency(totalAmount)}</span></div>
      <div class="summary-row"><span>VAT (0%):</span><span>${formatCurrency(0)}</span></div>
      <div class="summary-row total-row"><span>Total:</span><span>${formatCurrency(totalAmount)}</span></div>
      <div class="summary-row"><span>Amount Paid:</span><span>${formatCurrency(amountPaid)}</span></div>
      <div class="summary-row"><span>Remaining Amount:</span><span>${formatCurrency(remainingAmount)}</span></div>
      <div class="summary-row"><span>Payment Status:</span><span>${status?.name || "Pending"}</span></div>
    </div>
  </div>

  <!-- Footer -->
  <div class="footer">Generated on: ${generatedDate}</div>

</body>
</html>
`;

  const element = document.createElement("div");
  element.innerHTML = htmlContent;

  const options = {
    margin: [8, 8, 12, 8],
    filename: `SellerInvoice_${invoice.id}.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2, useCORS: true, logging: false },
    jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
  };

  if (mode === "view") {
    html2pdf()
      .set(options)
      .from(element)
      .toPdf()
      .get("pdf")
      .then((pdf) => {
        const url = pdf.output("bloburl");
        window.open(url, "_blank");
      });
  } else {
    html2pdf().set(options).from(element).save();
  }
};
